"use client";

import ReorderGroupWrapper from "../ReorderGroupWrapper";
import RichTextInput from "@/components/ui/RichTextInput";
import ProductSection from "./EditProductSection";
import { ProductClientState } from "@/zod/product.schema";
import { useFormContext, useWatch } from "react-hook-form";

interface KeyFeaturesSectionProps {
  currentLang: "en" | "fa";
  isOpen: boolean;
  onToggle: () => void;
  submitAttempted?: boolean;
}

export function KeyFeaturesSection({
  currentLang,
  isOpen,
  onToggle,
  submitAttempted = false,
}: KeyFeaturesSectionProps) {
  const { control, setValue, formState } = useFormContext<ProductClientState>();

  const values = useWatch({ control, name: "key_features" });

  const invalid = !!formState.errors.key_features;
  const dir = currentLang === "fa" ? "rtl" : "ltr";
  const editorAlign = currentLang === "fa" ? "text-right" : "text-left";

  return (
    <ProductSection
      title="Key Features"
      faTitle="ویژگی ها و مزایای کلیدی"
      showFa={currentLang === "fa"}
      dir={dir}
      textAlign={currentLang === "fa" ? "justify" : "left"}
      open={isOpen}
      onToggle={onToggle}
      invalid={submitAttempted && invalid}
    >
      <ReorderGroupWrapper<{ value: { text_en: string; text_fa: string } }>
        initialItems={(values ?? []).map((i) => ({
          value: {
            text_en: i.text_en,
            text_fa: i.text_fa ?? "",
          },
        }))}
        newItemTemplate={{ value: { text_en: "", text_fa: "" } }}
        confirmDeleteMessage={
          currentLang === "fa"
            ? "با حذف این مورد، متن انگلیسی و فارسی آن هر دو حذف می‌شوند. ادامه می‌دهید؟"
            : "Deleting this item will remove both EN and FA texts. Continue?"
        }
        onChange={(vals) =>
          setValue(
            "key_features",
            vals.map((val) => val.value),
          )
        }
        renderItem={(item, index, update) => (
          <div>
            <RichTextInput
              placeholder={
                currentLang === "fa"
                  ? "ویژگی ها و مزایای کلیدی"
                  : "Key Features"
              }
              // ✅ soft-migration: pass legacy HTML/text or JSON string directly
              value={item.value[`text_${currentLang}`] ?? ""}
              // ✅ always save JSON-string going forward
              onChange={({ json }) =>
                update({
                  value: {
                    ...item.value,
                    [`text_${currentLang}`]: JSON.stringify(json),
                  },
                })
              }
              className="rounded-md border border-gray-200 bg-white"
              editorClassName={editorAlign}
              dir={dir as "ltr" | "rtl"}
              minHeightClassName="min-h-[4rem]"
            />

            {submitAttempted && (
              <p className="mt-1 text-xs text-red-500">
                {(formState.errors as any)?.key_features?.[index]?.text_en
                  ?.message ||
                  (formState.errors as any)?.key_features?.[index]?.text_fa
                    ?.message ||
                  ""}
              </p>
            )}
          </div>
        )}
      />
    </ProductSection>
  );
}
